import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppServices } from "../../../../../../../hook/services";
import { toast } from "react-toastify";
import { MMStyles } from "../AddNewMenuModal";

const USER_ROLES = [
  "agency_admins",
  "agency_users",
  "account_admins",
  "account_users",
];

const MenuHideModal = ({ selectedItem, data, isDisabled, handleRefresh }) => {
  const AppService = useAppServices();
  const { companyId, type } = useParams();
  const handler =
    type === "agency" || type === "location"
      ? AppService.sidenav_customizer_main_level
      : type === "group"
      ? AppService.sidenav_customizer_custom_level
      : AppService.sidenav_customizer_saas_level;

  const [hiddenItems, setHiddenItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [permissions, setPermissions] = useState(() =>
    USER_ROLES.reduce((acc, role) => ({ ...acc, [role]: false }), {})
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (data?.menus) {
      setHiddenItems(data.menus);
    }
  }, [data]);

  const isItemHidden = selectedItem?.type == "hide";

  const resetPermissions = () =>
    setPermissions(
      USER_ROLES.reduce((acc, role) => ({ ...acc, [role]: false }), {})
    );

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
    resetPermissions();
  };

  const handlePermissionsChange = (e) => {
    const { name, checked } = e.target;
    setPermissions((prev) => ({ ...prev, [name]: checked }));
  };

  const handleHideClick = async () => {
    if (!selectedItem?.value) {
      toast.warning("No menu item selected.");
      return;
    }
    setSubmitting(true);

    const updatedHiddenItems = [...hiddenItems];

    let existingIndex = updatedHiddenItems.findIndex(
      (edit) => edit.selector === selectedItem.selector
    );

    if (existingIndex !== -1) {
      if (isItemHidden) {
        updatedHiddenItems[existingIndex] = {
          ...updatedHiddenItems[existingIndex],
          type: null,
          permissions: null,
        };
      } else {
        updatedHiddenItems[existingIndex] = {
          ...updatedHiddenItems[existingIndex],
          type: "hide",
          permissions: { user_permissions: permissions },
        };
      }
    } else {
      const parentIndex = updatedHiddenItems.findIndex(
        (item) =>
          Array.isArray(item.children) &&
          item.children.some(
            (child) => child.selector === selectedItem.selector
          )
      );

      if (parentIndex !== -1) {
        const childIndex = updatedHiddenItems[parentIndex].children.findIndex(
          (child) => child.selector === selectedItem.selector
        );

        if (childIndex !== -1) {
          if (isItemHidden) {
            updatedHiddenItems[parentIndex].children[childIndex] = {
              ...updatedHiddenItems[parentIndex].children[childIndex],
              type: null,
              permissions: null,
            };
          } else {
            updatedHiddenItems[parentIndex].children[childIndex] = {
              ...updatedHiddenItems[parentIndex].children[childIndex],
              type: "hide",
              permissions: { user_permissions: permissions },
            };
          }
        }
      }
    }

    const payload = {
      companyId,
      menus: updatedHiddenItems,
      type,
      ...(data?._id && { _id: data._id }),
    };

    try {
      const { response } = await handler[data?._id ? "update" : "create"]({
        payload,
      });

      if (response) {
        setHiddenItems(updatedHiddenItems);
        handleClose();
        handleRefresh();

        toast.success(
          isItemHidden
            ? "Menu item unhidden successfully."
            : "Menu item hidden successfully."
        );
      } else {
        toast.error("Failed to update menu visibility.");
      }
    } catch (error) {
      console.error("Error hiding/unhiding menu:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={`mm-icon-btn ${isItemHidden ? "is-active" : ""}`}
        title={isItemHidden ? "Unhide menu" : "Hide menu"}
        disabled={isDisabled}
        onClick={isItemHidden ? handleHideClick : handleOpen}
      >
        {isItemHidden ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
            <path d="M14.12 14.12A3 3 0 1 1 9.88 9.88" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>

      {open && (
        <div className="mm-overlay" onMouseDown={handleClose}>
          <div
            className="mm-dialog"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <header className="mm-header d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mm-title mb-0">Hide menu item</h5>
                <p className="mm-subtitle mb-0">Choose which user roles will no longer see this menu.</p>
              </div>
              <button type="button" className="mm-close" onClick={handleClose} aria-label="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </header>

            <div className="mm-body">
              <div className="mm-item-card d-flex align-items-center gap-3 mb-3">
                <div className="mm-item-card-icon d-flex align-items-center justify-content-center">
                  {selectedItem?.newLabel ? (
                    <i
                      class="fa"
                      dangerouslySetInnerHTML={{ __html: selectedItem?.icon }}
                    ></i>
                  ) : (
                    <span>{selectedItem.icon}</span>
                  )}
                </div>
                <div className="flex-grow-1">
                  <div className="mm-item-card-label">{selectedItem?.newLabel || selectedItem.label}</div>
                  <div className="mm-item-card-meta">Menu item</div>
                </div>
              </div>

              <div className="mm-field mb-0">
                <label className="mm-label">Hide for</label>
                <div className="mm-checkboxes">
                  {USER_ROLES.map((role) => (
                    <label className="mm-checkbox" key={role}>
                      <input
                        type="checkbox"
                        name={role}
                        checked={permissions[role]}
                        onChange={handlePermissionsChange}
                      />
                      <span className="mm-checkbox-box" aria-hidden="true">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </span>
                      <span className="text-capitalize">{role.replace("_", " ")}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <footer className="mm-footer d-flex justify-content-end gap-2">
              <button type="button" className="mm-btn mm-btn--ghost" onClick={handleClose} disabled={submitting}>
                Cancel
              </button>
              <button type="button" className="mm-btn mm-btn--danger" onClick={handleHideClick} disabled={submitting}>
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                    Hiding
                  </>
                ) : (
                  "Hide menu"
                )}
              </button>
            </footer>
          </div>
        </div>
      )}

      <MMStyles />
      <MenuHideExtras />
    </>
  );
};

const MenuHideExtras = () => (
  <style>{`
    .mm-icon-btn {
      width: 30px;
      height: 30px;
      border: 1px solid #e5e7eb;
      background: #fff;
      color: #4b5563;
      border-radius: 7px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
      transition: transform 0.15s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.2s ease;
    }
    .mm-icon-btn:hover:not(:disabled) {
      background: #fafafa;
      border-color: #d1d5db;
      color: #1f2937;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(17, 24, 39, 0.05), 0 4px 10px rgba(17, 24, 39, 0.06);
    }
    .mm-icon-btn:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.05);
    }
    .mm-icon-btn:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2), 0 4px 10px rgba(79, 70, 229, 0.18);
    }
    .mm-icon-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .mm-icon-btn.is-active {
      background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
      border-color: rgba(255, 255, 255, 0.04);
      color: #fff;
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.18) inset,
        0 1px 2px rgba(79, 70, 229, 0.22),
        0 3px 8px rgba(79, 70, 229, 0.22);
    }
    .mm-icon-btn.is-active:hover:not(:disabled) {
      background: linear-gradient(180deg, #4f46e5 0%, #4338ca 100%);
      transform: translateY(-1px);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.18) inset,
        0 2px 4px rgba(67, 56, 202, 0.28),
        0 6px 14px rgba(79, 70, 229, 0.3);
    }

    .mm-item-card {
      border: 1px solid #ececec;
      border-radius: 10px;
      background: #fafafa;
      padding: 12px 14px;
    }
    .mm-item-card-icon {
      width: 38px;
      height: 38px;
      border-radius: 8px;
      background: #fff;
      border: 1px solid #ececec;
      color: #1f2937;
      flex-shrink: 0;
    }
    .mm-item-card-label {
      font-size: 0.875rem;
      font-weight: 600;
      color: #1f2937;
    }
    .mm-item-card-meta {
      font-size: 0.75rem;
      color: #9ca3af;
      margin-top: 1px;
    }
  `}</style>
);

export { MenuHideExtras };
export default MenuHideModal;
